import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDownIcon } from "lucide-react";
import { twCn } from "@/styles/utils";

export type AccordionType = "single" | "multiple";

export type AccordionBootstrapProps<T extends AccordionType> =
  T extends "single"
    ? AccordionPrimitive.AccordionSingleProps
    : AccordionPrimitive.AccordionMultipleProps;

function AccordionBootstrap<T extends AccordionType>({
  ...props
}: AccordionBootstrapProps<T>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={twCn(
        "bg-gray-bg data-[state=open]:bg-brand-light duration-var([--animation-duration-standard]) rounded-2xl transition-colors",
        className,
      )}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={twCn(
          "lgc:text-xl flex flex-1 items-start justify-between gap-4 px-8 pt-6 text-left font-bold text-black transition-all outline-none disabled:pointer-events-none disabled:opacity-50",
          "data-[state=open]:text-brand-text-primary data-[state=closed]:pb-6 [&[data-state=open]>svg]:rotate-180",
          className,
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon className="pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-[var(--animation-duration-standard)]" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down lgc:mt-4 text-brand-text-secondary mt-2 overflow-hidden px-8 pb-6"
      {...props}
    >
      <div className={className}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export {
  AccordionBootstrap,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
};
